// ModernFileViewer.jsx
import { useState, useEffect } from 'react';
import { File, Download, ExternalLink, AlertCircle, ArrowLeft } from 'lucide-react';
import clsx from 'clsx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
// GEEN remark-breaks hier (anders krijg je extra <br/> midden in zinnen)
import rehypeKatex from 'rehype-katex';

export function ModernFileViewer({ file, user, onBack, isMobile }) {
  const [fileContent, setFileContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!file || !user) return;

    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const ext = getFileType(file.name);

        if (ext === 'pdf') {
          setFileContent('PDF_FILE');
          return;
        }

        const response = await fetch(file.url, { credentials: 'include' });
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

        let content;
        if (ext === 'json') {
          const data = await response.json();
          content = JSON.stringify(data, null, 2);
        } else {
          content = await response.text();
        }

        if (!cancelled) setFileContent(content);
      } catch (err) {
        console.error('Error loading file:', err);
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [file, user]);

  function getFileType(filename) {
    const ext = (filename?.split('.').pop() || '').toLowerCase();
    return ext || 'unknown';
  }

  function isMarkdownType(ext) {
    return ['md', 'markdown', 'mdown', 'mkd', 'mkdown'].includes(ext);
  }

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return 'Onbekend';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(Math.max(bytes, 1)) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Onbekend';
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const handleDownload = () => { if (file?.url) window.open(file.url, '_blank'); };
  const handleOpenExternal = () => { if (file?.url) window.open(file.url, '_blank'); };

  if (!file) {
    return (
      <div className="h-full bg-white dark:bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <File className="h-16 w-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Geen bestand geselecteerd</h3>
          <p className="text-gray-500 dark:text-gray-400">Selecteer een bestand uit de lijst om de inhoud te bekijken</p>
        </div>
      </div>
    );
  }

  const ext = getFileType(file.name);
  const isPdf = ext === 'pdf';
  const isMd = isMarkdownType(ext);
  const isText = ['txt','csv','json','xml','html','css','js','py','java','cpp','c','h'].includes(ext) || isMd;

  return (
    <div className="h-full bg-white dark:bg-dark-900 flex flex-col">
      <div className="p-6 border-b border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-800/50">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {isMobile && onBack && (
              <button
                onClick={onBack}
                className="flex-shrink-0 p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
                title="Terug naar bestanden"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate mb-2">{file.name}</h2>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                <span>{formatFileSize(file.size)}</span><span>•</span>
                <span>{formatDate(file.uploaded_at)}</span><span>•</span>
                <span className="capitalize">{ext.toUpperCase()}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleDownload} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors" title="Downloaden">
              <Download className="h-5 w-5" />
            </button>
            <button onClick={handleOpenExternal} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors" title="Openen in nieuw tabblad">
              <ExternalLink className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {isLoading ? <LoadingState />
          : error ? <ErrorState error={error} onRetry={() => {}} />
          : isPdf ? <PDFViewer file={file} />
          : isText ? <TextViewer content={fileContent} isMarkdown={isMd} />
          : <UnsupportedFileType file={file} />}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="h-full flex items-center justify-center">
      <p className="text-gray-600 dark:text-gray-400">Bestand laden...</p>
    </div>
  );
}

function ErrorState({ error, onRetry }) {
  return (
    <div className="h-full flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Fout bij laden van bestand</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
        <button onClick={onRetry} className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">
          Opnieuw proberen
        </button>
      </div>
    </div>
  );
}

function PDFViewer({ file }) {
  return (
    <iframe src={`${file.url}#view=FitH`} className="w-full h-full border-0" title={file.name} />
  );
}

function TextViewer({ content, isMarkdown }) {
  // Converteer letterlijsten (a./b./c. of a)/b)/c)) naar genummerde lijst-syntax,
  // zodat ReactMarkdown er echte <ol><li>…</li></ol> van maakt.
  // We tonen ze vervolgens als letters met CSS (list-[lower-alpha]).
  const normalizeAlphaOrderedLists = (input) => {
    if (!input) return '';
    const lines = input.split('\n');
    const out = [];
    let i = 0;

    const alphaPattern = /^(\s*)([A-Za-z])([.)])\s+(.*)$/; // indent + letter + .|) + sp + tekst

    while (i < lines.length) {
      const m = lines[i].match(alphaPattern);
      if (m) {
        const indent = m[1];
        const block = [];
        let j = i;
        while (j < lines.length) {
          const m2 = lines[j].match(alphaPattern);
          if (!m2 || m2[1] !== indent) break;
          block.push(m2[4]);
          j++;
        }
        // Schrijf het blok als 1.,2.,3. — we stylen straks als letters
        for (let k = 0; k < block.length; k++) {
          out.push(`${indent}${k + 1}. ${block[k]}`);
        }
        i = j;
        continue;
      }
      out.push(lines[i]);
      i++;
    }
    return out.join('\n');
  };

  // GEEN remark-breaks: zo blijven "hard wraps" netjes samenvallen tot spaties
  const preprocessed = isMarkdown ? normalizeAlphaOrderedLists(content) : content;

  if (isMarkdown) {
    return (
      <div className="h-full overflow-y-auto scrollbar-thin p-6 prose dark:prose-invert max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeKatex]}
          components={{
            h1: (props) => <h1 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 dark:text-white" {...props} />,
            h2: (props) => <h2 className="text-xl md:text-2xl font-semibold mb-3 text-gray-900 dark:text-white" {...props} />,
            h3: (props) => <h3 className="text-lg md:text-xl font-medium mb-2 text-gray-900 dark:text-white" {...props} />,
            h4: (props) => <h4 className="text-base md:text-lg font-semibold mb-2 text-gray-900 dark:text-white" {...props} />,
            p:  (props) => <p className="text-gray-800 dark:text-gray-300 mb-3 leading-relaxed" {...props} />,
            ul: (props) => <ul className="list-disc pl-6 mb-3 space-y-1" {...props} />,
            // 👉 toon ALLE geordende lijsten als letters
            ol: (props) => <ol className="list-[lower-alpha] pl-6 mb-3 space-y-1" {...props} />,
            li: (props) => <li className="text-gray-800 dark:text-gray-300" {...props} />,
            strong: (props) => <strong className="text-gray-900 dark:text-white font-semibold" {...props} />,
            table: (props) => <table className="table-auto w-full border border-gray-300 dark:border-dark-700 text-sm text-left mb-4" {...props} />,
            thead: (props) => <thead className="bg-gray-100 dark:bg-dark-700 text-gray-900 dark:text-white" {...props} />,
            tbody: (props) => <tbody className="divide-y divide-gray-200 dark:divide-dark-700" {...props} />,
            tr: (props) => <tr className="border-b border-gray-200 dark:border-dark-700" {...props} />,
            th: (props) => <th className="px-4 py-2 font-medium border border-gray-300 dark:border-dark-600 bg-gray-50 dark:bg-dark-800 text-gray-800 dark:text-white" {...props} />,
            td: (props) => <td className="px-4 py-2 border border-gray-300 dark:border-dark-700 text-gray-700 dark:text-gray-300" {...props} />,
            code: ({ inline, className, children, ...props }) =>
              inline
                ? <code className="px-1 py-0.5 rounded bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200" {...props}>{children}</code>
                : <pre className="p-3 rounded bg-gray-900 text-gray-100 overflow-auto my-3"><code className={className} {...props}>{children}</code></pre>,
          }}
        >
          {preprocessed}
        </ReactMarkdown>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto scrollbar-thin">
      <pre className={clsx(
        "p-6 text-sm font-mono leading-relaxed whitespace-pre-wrap break-words",
        "text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-dark-800/50"
      )}>
        <code>{content}</code>
      </pre>
    </div>
  );
}

function UnsupportedFileType({ file }) {
  return (
    <div className="h-full flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <File className="h-16 w-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Voorvertoning niet beschikbaar</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Dit bestandstype kan niet worden weergegeven in de browser. Je kunt het downloaden om het te openen.
        </p>
        <button
          onClick={() => window.open(file.url, '_blank')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
        >
          <Download className="h-4 w-4" />
          Downloaden
        </button>
      </div>
    </div>
  );
}
